import React, { useEffect, useState, useCallback } from "react";
import { Star, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl } from "../lib/api";

const Stars = ({ value, onChange, size = "h-4 w-4" }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} className={onChange ? "cursor-pointer" : "cursor-default"} data-testid={onChange ? `review-star-${n}` : undefined}>
        <Star className={`${size} ${n <= value ? "text-neon-green fill-neon-green" : "text-zinc-700"}`} />
      </button>
    ))}
  </div>
);

export const ProductReviews = ({ productId }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ author: "", phone: "", rating: 5, text: "" });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(console.error);
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim()) { toast.error("Ton numéro est requis (vérification client)"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("catalog_id", productId);
      fd.append("phone", form.phone);
      fd.append("author", form.author);
      fd.append("rating", String(form.rating));
      fd.append("text", form.text);
      if (photo) fd.append("photo", photo);
      await api.post("/product-reviews", fd);
      toast.success("Merci pour ton avis ✓");
      setOpen(false); setPhoto(null); setForm({ author: "", phone: "", rating: 5, text: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setSending(false); }
  };

  const reviews = data?.reviews || [];
  return (
    <div className="border-t border-zinc-800 pt-4" data-testid="product-reviews">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-neon-green text-xs uppercase font-bold">
          Avis vérifiés clients {data?.count ? <span className="text-white ml-1">★ {data.avg} ({data.count})</span> : <span className="text-zinc-500 normal-case font-normal ml-1">— sois le premier à noter</span>}
        </p>
        <button type="button" data-testid="review-open-form" onClick={() => setOpen(!open)} className="text-xs uppercase font-bold text-neon-magenta hover:text-white shrink-0">{open ? "Fermer" : "Laisser un avis"}</button>
      </div>
      {open && (
        <form onSubmit={submit} className="border border-zinc-800 bg-ink p-3 mb-3 space-y-2" data-testid="review-form">
          <div className="flex items-center gap-2"><span className="text-zinc-400 text-xs">Ta note :</span><Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size="h-5 w-5" /></div>
          <div className="flex gap-2">
            <input data-testid="review-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Ton prénom" className="w-1/2 bg-ink-surface border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-green" />
            <input data-testid="review-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Ton n° de commande *" className="w-1/2 bg-ink-surface border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-green" />
          </div>
          <textarea data-testid="review-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={2} maxLength={600} placeholder="Ton retour sur ce produit…" className="w-full bg-ink-surface border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-green" />
          <label className="flex items-center gap-2 text-zinc-400 text-xs cursor-pointer w-fit">
            <Camera className="h-4 w-4 text-neon-magenta" /> {photo ? photo.name : "Ajouter une photo (optionnel)"}
            <input data-testid="review-photo" type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
          </label>
          <button data-testid="review-submit" disabled={sending} className="bg-neon-magenta text-white font-anton uppercase text-sm px-4 py-2 disabled:opacity-40">{sending ? "Envoi…" : "Publier mon avis"}</button>
          <p className="text-zinc-600 text-[11px]">Seuls les clients ayant reçu ce produit peuvent noter (vérification par n° de téléphone utilisé à la commande).</p>
        </form>
      )}
      {reviews.length > 0 && (
        <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
          {reviews.map((r) => (
            <div key={r.id} className="border border-zinc-800 bg-ink p-3" data-testid="review-item">
              <div className="flex items-center justify-between mb-1">
                <span className="text-white text-sm font-semibold flex items-center gap-2">✓ {r.author} <span className="text-neon-green border border-neon-green/40 text-[9px] uppercase font-bold px-1.5 py-0.5">Achat vérifié</span></span>
                <Stars value={r.rating} />
              </div>
              {r.text && <p className="text-zinc-400 text-sm">{r.text}</p>}
              {r.photo_url && (
                <a href={imageUrl(r.photo_url)} target="_blank" rel="noopener noreferrer">
                  <img src={imageUrl(r.photo_url)} alt={`Photo client — ${r.author}`} className="mt-2 h-20 w-20 object-cover border border-zinc-700 hover:border-neon-green" loading="lazy" data-testid="review-photo-thumb" />
                </a>
              )}
              <p className="text-zinc-600 text-[11px] mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
